import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, fecha, buscar } from '../utils/formato';

// Los inmuebles.
//
// El inmueble vive más que sus inquilinos, y por eso es una ficha aparte del
// contrato. Así la historia de lo que rindió y de lo que costó arreglarlo se
// acumula sola aunque cambie diez veces de arrendatario.
//
// La pantalla abre por el estado y no por la lista: lo que se quiere saber al
// entrar es qué está vacío, porque eso es lo que está costando plata.

const VACIO = {
  nombre: '', direccion: '', tipo: 'local', area: '', matricula: '',
  predial_anual: '', administracion_mensual: '', valor_compra: '',
  fecha_compra: '', nota: '',
};

const TIPOS = [
  { id: 'local',       nombre: 'Local comercial' },
  { id: 'vivienda',    nombre: 'Vivienda' },
  { id: 'bodega',      nombre: 'Bodega' },
  { id: 'parqueadero', nombre: 'Parqueadero' },
];

function Inmuebles({ irA, onCambio }) {
  const [lista, setLista] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = useCallback(() => {
    axios.get('/api/inmuebles/estado').then(r => setLista(r.data || [])).catch(() => {});
  }, []);

  useEffect(cargar, [cargar]);

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const datos = {
        ...editando,
        area: parseFloat(editando.area) || null,
        predial_anual: parseFloat(editando.predial_anual) || 0,
        administracion_mensual: parseFloat(editando.administracion_mensual) || 0,
        valor_compra: parseFloat(editando.valor_compra) || 0,
      };
      if (editando.id) await axios.put(`/api/inmuebles/${editando.id}`, datos);
      else await axios.post('/api/inmuebles', datos);
      setAviso({ tipo: 'bueno', texto: `${editando.nombre} quedó guardado` });
      setEditando(null);
      cargar();
      onCambio && onCambio();
    } catch (err) {
      setAviso({ tipo: 'malo', texto: err.response?.data?.error || 'No se pudo guardar' });
    }
    setGuardando(false);
  };

  const visibles = buscar(lista, busqueda, ['nombre', 'direccion', 'tipo']);
  const vacios = lista.filter(i => i.estado === 'vacio');
  const costoVacios = vacios.reduce((s, i) => s + (i.costo_mensual || 0), 0);
  const enArriendo = lista.filter(i => i.estado === 'arrendado');
  const rentaMes = enArriendo.reduce(
    (s, i) => s + (i.contrato?.estado_cuenta?.canon_vigente || 0), 0);

  return (
    <>
      <div className="encabezado">
        <h1>Inmuebles</h1>
        <p>Qué está arrendado, qué está vacío y desde cuándo</p>
      </div>

      {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

      <div className="rejilla">
        <div className="dato" style={{ '--color': 'var(--acero)' }}>
          <div className="rotulo">🏢 Inmuebles</div>
          <div className="valor cifra">{lista.length}</div>
          <div className="pie">{enArriendo.length} arrendados</div>
        </div>
        <div className="dato" style={{ '--color': 'var(--venta)' }}>
          <div className="rotulo">📅 Renta al mes</div>
          <div className="valor cifra">{plata(rentaMes)}</div>
          <div className="pie">con los contratos de hoy</div>
        </div>
        <div className="dato" style={{ '--color': vacios.length ? 'var(--malo)' : 'var(--bueno)' }}>
          <div className="rotulo">🕳️ Vacíos</div>
          <div className="valor cifra">{vacios.length}</div>
          <div className="pie">
            {costoVacios > 0 ? `cuestan ${plata(costoVacios)}/mes` : 'ninguno'}
          </div>
        </div>
      </div>

      <div className="bloque">
        <h2>🏢 Los inmuebles <span className="contador">{lista.length}</span></h2>
        <div className="cuerpo">
          <div className="acciones" style={{ marginBottom: 14 }}>
            <button className="boton" onClick={() => setEditando({ ...VACIO })}>
              ➕ Nuevo inmueble
            </button>
          </div>

          {lista.length > 4 && (
            <div className="buscador">
              <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
                     placeholder="🔎 Buscar por nombre o dirección" />
              {busqueda && <button type="button" onClick={() => setBusqueda('')}>×</button>}
            </div>
          )}

          {visibles.length === 0 ? (
            <div className="vacio">
              {lista.length === 0
                ? 'Todavía no hay inmuebles. Crea el primero para poder hacerle un contrato.'
                : 'Ninguno coincide con esa búsqueda.'}
            </div>
          ) : (
            <div className="tabla-caja">
              <table className="datos">
                <thead>
                  <tr>
                    <th>Inmueble</th>
                    <th>Tipo</th>
                    <th>Estado</th>
                    <th style={{ textAlign: 'right' }}>Canon</th>
                    <th style={{ textAlign: 'right' }}>Le cuesta</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map(i => (
                    <tr key={i.id}>
                      <td className="recio">
                        {i.nombre}
                        {i.direccion && (
                          <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                            {i.direccion}
                          </div>
                        )}
                      </td>
                      <td><span className="pinta gris">{i.tipo}</span></td>
                      <td>
                        {i.estado === 'arrendado' ? (
                          <>
                            <span className="pinta bueno">arrendado</span>
                            <div style={{ fontSize: 11, color: 'var(--tinta-tenue)', marginTop: 3 }}>
                              {i.contrato.arrendatario}
                            </div>
                          </>
                        ) : (
                          <>
                            <span className="pinta malo">vacío</span>
                            <div style={{ fontSize: 11, color: 'var(--tinta-tenue)', marginTop: 3 }}>
                              {i.dias_vacio != null
                                ? `hace ${i.dias_vacio} ${i.dias_vacio === 1 ? 'día' : 'días'}`
                                : 'nunca arrendado'}
                            </div>
                          </>
                        )}
                      </td>
                      <td className="num">
                        {i.contrato
                          ? plata(i.contrato.estado_cuenta.canon_vigente)
                          : <span style={{ color: 'var(--tinta-tenue)' }}>—</span>}
                      </td>
                      <td className="num" style={{ color: 'var(--malo)' }}>
                        {i.costo_mensual > 0 ? plata(i.costo_mensual) : '—'}
                        <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                          al mes
                        </div>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="boton chico suave"
                                onClick={() => setEditando({ ...VACIO, ...i })}>
                          ✏️
                        </button>{' '}
                        {i.estado === 'vacio' && (
                          <button className="boton chico" onClick={() => irA('contratos')}>
                            Arrendar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editando && (
        <div className="bloque">
          <h2>{editando.id ? '✏️ Editar inmueble' : '➕ Nuevo inmueble'}</h2>
          <form onSubmit={guardar}>
            <div className="cuerpo">
              <div className="campos">
                <label className="campo">
                  <span>Cómo lo llama *</span>
                  <input value={editando.nombre} required autoFocus
                         placeholder="Local 3 de la 15, Apto 402…"
                         onChange={e => setEditando({ ...editando, nombre: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Tipo</span>
                  <select value={editando.tipo}
                          onChange={e => setEditando({ ...editando, tipo: e.target.value })}>
                    {TIPOS.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                  </select>
                </label>
                <label className="campo">
                  <span>Dirección</span>
                  <input value={editando.direccion || ''}
                         onChange={e => setEditando({ ...editando, direccion: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Área (m²)</span>
                  <input type="number" step="0.01" min="0" value={editando.area || ''}
                         onChange={e => setEditando({ ...editando, area: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Predial al año</span>
                  <input type="number" step="1" min="0" value={editando.predial_anual || ''}
                         placeholder="0"
                         onChange={e => setEditando({ ...editando, predial_anual: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Administración al mes</span>
                  <input type="number" step="1" min="0" value={editando.administracion_mensual || ''}
                         placeholder="0"
                         onChange={e => setEditando({ ...editando, administracion_mensual: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Cuánto costó el inmueble</span>
                  <input type="number" step="1" min="0" value={editando.valor_compra || ''}
                         placeholder="0"
                         onChange={e => setEditando({ ...editando, valor_compra: e.target.value })} />
                </label>
                <label className="campo">
                  <span>Matrícula inmobiliaria</span>
                  <input value={editando.matricula || ''}
                         onChange={e => setEditando({ ...editando, matricula: e.target.value })} />
                </label>
              </div>

              <p style={{ fontSize: 13, color: 'var(--tinta-tenue)', marginTop: 14 }}>
                El <strong>predial</strong> y la <strong>administración</strong> corren
                esté arrendado o no: son los que convierten un mes vacío en pérdida en vez
                de en cero. Y <strong>cuánto costó el inmueble</strong> es lo que permite
                decirle qué rentabilidad le está dando.
              </p>

              <div className="acciones">
                <button type="button" className="boton suave" onClick={() => setEditando(null)}>
                  Cancelar
                </button>
                <button className="boton" disabled={guardando}>
                  {guardando ? 'Guardando…' : '💾 Guardar'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default Inmuebles;
